import type { Vec2 } from "./types";
import { boundingBox } from "./geom";

/**
 * Geometry helpers for user-authored furniture outlines (the "furniture
 * creator" experience).
 *
 * A furniture outline is stored on {@link import("./types").Furniture.shape}
 * (and {@link import("./types").CustomFurnitureItem.shape}) as a closed polygon
 * normalized into the unit box `[-0.5, 0.5]` on both axes and centered at the
 * footprint origin. The footprint `width`/`depth` (the outline's bounding box
 * in inches) remain the dimensional source of truth, so resizing the piece
 * simply rescales the normalized outline.
 */

export interface NormalizedOutline {
  /** Normalized closed polygon, each component in `[-0.5, 0.5]`. */
  shape: Vec2[];
  /** Bounding-box width of the original outline, inches. */
  width: number;
  /** Bounding-box height (depth) of the original outline, inches. */
  depth: number;
}

/**
 * Normalize a sketched polygon (in inches) into the unit box, returning the
 * normalized outline plus the footprint `width`/`depth` taken from its
 * bounding box.
 *
 * Degenerate inputs (fewer than 3 points, or a zero-width/height bounding box)
 * yield a `null` result so callers can reject the sketch rather than persist a
 * collapsed shape.
 */
export function normalizeOutline(points: Vec2[]): NormalizedOutline | null {
  if (points.length < 3) return null;
  const bb = boundingBox(points);
  if (
    !Number.isFinite(bb.width) ||
    !Number.isFinite(bb.height) ||
    bb.width <= 0 ||
    bb.height <= 0
  ) {
    return null;
  }
  const cx = bb.minX + bb.width / 2;
  const cy = bb.minY + bb.height / 2;
  const shape = points.map((p) => ({
    x: (p.x - cx) / bb.width,
    y: (p.y - cy) / bb.height,
  }));
  return { shape, width: bb.width, depth: bb.height };
}

/**
 * Expand a normalized outline back into footprint-relative coordinates
 * (inches, centered at origin) by scaling each component by `width`/`depth`.
 * Used by the canvas renderer and the picker thumbnail.
 */
export function denormalizeOutline(
  shape: Vec2[],
  width: number,
  depth: number
): Vec2[] {
  return shape.map((p) => ({ x: p.x * width, y: p.y * depth }));
}

/**
 * Flatten a normalized outline into a Konva `Line` points array, scaled by a
 * pixels-per-inch conversion. Each point is `[x0, y0, x1, y1, ...]` in the
 * footprint-local coordinate space (origin at center), ready to drop into a
 * `<Line points={...} closed />` inside a rotated `Group`.
 */
export function outlineToFlatPx(
  shape: Vec2[],
  widthPx: number,
  depthPx: number
): number[] {
  const arr: number[] = [];
  for (const p of shape) {
    arr.push(p.x * widthPx, p.y * depthPx);
  }
  return arr;
}
